/**
 * 传输层失败归类内核（FE-E2b 下沉，FE1-014/FE2-006）：TRANSPORT_MESSAGES +
 * classifyTransport 单一真源。admin/agent 的 apiErrorMessages 消费（merchant/customer
 * 的 mapApiError 形态差异超参数化能力，保留本地实现——见 FE-E2b 报告迁/keep 清单）。
 *
 * 归类口径（对齐各端 client 实际产出形态）：
 * - rawRequest/rawUpload/rawDownload catch → e.message（'Failed to fetch' /
 *   'Load failed' / 'NetworkError when attempting to fetch resource.'）或 'network_error'
 * - parseRaw JSON 解析失败 → res.ok ? 'invalid_json' : `HTTP <status>`
 * - parseRaw 无信封 code 的非 2xx → String(res.status)（如 '403'）
 */

/** 传输层失败（code === undefined）时 error 字符串的归类文案 */
export const TRANSPORT_MESSAGES = {
  /** 连接失败（浏览器网络异常消息 / client 兜底 network_error） */
  offline: '网络连接异常，请检查网络后重试',
  /** 中断 / 超时（client 无显式 timeout，Abort 类按超时防御性归类） */
  timeout: '请求超时，请检查网络后重试',
  /** HTTP 200 但响应体非 JSON（parseRaw invalid_json 分支） */
  invalidJson: '服务响应异常，请稍后重试',
  /** HTTP 5xx（parseRaw `HTTP <status>` / 无信封纯数字） */
  serverError: '服务暂时不可用，请稍后重试',
  /** HTTP 4xx 且无业务信封（网关拒绝等） */
  rejected: '请求被服务端拒绝，请刷新后重试或联系管理员',
  /** 未识别形态（含未知异常消息） */
  unknown: '网络异常或服务不可用，请稍后重试',
} as const;

/** 端文案覆盖点：agent 端 rejected 文案为「联系平台客服」（与本端 client 既有文案一致），
 * admin/merchant/customer 用缺省（「联系管理员」）；归类逻辑单一真源不受覆盖影响。 */
type TransportMessages = { readonly [K in keyof typeof TRANSPORT_MESSAGES]: string };

/** 以端文案集构造分类器（缺省 = TRANSPORT_MESSAGES；端仅覆盖文案，不改分流逻辑） */
export function classifyTransportWith(messages: TransportMessages): (error: string) => string {
  return (error: string): string => {
  const msg = error.trim();
  if (
    msg === 'network_error'
    || /^(failed to fetch|fetch failed|load failed)$/i.test(msg)
    || /^networkerror\b/i.test(msg)
    || /network.*(fail|unreachable|refused|reset)/i.test(msg)
    || /dns/i.test(msg)
  ) {
    return messages.offline;
  }
  if (/(timeout|timed?\s*out|abort)/i.test(msg)) {
    return messages.timeout;
  }
  if (msg === 'invalid_json') {
    return messages.invalidJson;
  }
  const httpStatus = msg.match(/^(?:HTTP\s+)?(\d{3})$/i);
  if (httpStatus) {
    const status = Number(httpStatus[1]);
    if (status >= 500) {
      return messages.serverError;
    }
    if (status >= 400) {
      return messages.rejected;
    }
  }
  if (/^HTTP\s+\d{3}/i.test(msg)) {
    return messages.serverError;
  }
  return messages.unknown;
  };
}

/** 缺省分类器（admin 文案集） */
export const classifyTransport: (error: string) => string = classifyTransportWith(TRANSPORT_MESSAGES);
